// /signin: an email gets a six-digit code (an unknown address becomes an
// account on the waitlist), or a passkey, or Google when configured.
//
// Promise:
// - Two steps on one page: the email, then the code. Six digits send by
//   themselves (paste, OS autofill). A send refused for "too soon" still goes
//   to the code step (the code sent a moment ago works for ten minutes) and the
//   resend waits for the time the server says.
// - Every way in ends in `finish`: inside an OAuth flow (the signed query on
//   this page) authorize runs again; otherwise the destination (waitlist,
//   passkey once, `next`, Me).
// - Reasons appear under what was pressed; the lines keep their height so
//   nothing moves when one appears.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { m } from "@trameria/cloud-i18n";
import { Button, buttonClass, useApi, useIsDark } from "@trameria/ui-core";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import {
  CODE_MINUTES,
  otpErrorMessage,
  passkeySignInErrorMessage,
  RESEND_COOLDOWN_SECONDS,
  RESEND_TOO_SOON,
  sendSignInCode,
  signInWithGoogle,
  signInWithPasskey,
  signInWithPasskeyAutofill,
  verifySignInCode,
} from "../auth/client";
import { CODE_LENGTH, looksLikeEmail, normalizeCode } from "../auth/code";
import { signInDestination } from "../auth/destination";
import { authorizeQueryFromSigned } from "../auth/oauth-query";
import { useTurnstile } from "../auth/turnstile";
import { useCloud } from "../context";
import { authConfigQuery } from "../queries";
import { MessageSlot, SignedOutPage, TEXT_BUTTON } from "./signed-out-page";

const FIELD =
  "plain-field h-10 w-full rounded-[7px] border border-field bg-surface px-3 text-[15px] text-fg outline-none focus:border-field-strong";

export function SignInPage({ next }: { next?: string }) {
  const api = useApi();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { session, bootSearch } = useCloud();
  const config = useQuery(authConfigQuery(api));
  const turnstile = useTurnstile(
    config.data?.turnstileSiteKey ?? null,
    useIsDark(),
  );
  const emailId = useId();
  const [email, setEmail] = useState("");
  /** Where the code went; null on the email step. */
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  /** The reason is about the typed code itself (paints the cells). */
  const [codeInvalid, setCodeInvalid] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /** The code step shows a code sent earlier (the server refused a new one). */
  const [reusedCode, setReusedCode] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resendReadyAt, setResendReadyAt] = useState<number | null>(null);
  const [resendWait, setResendWait] = useState(0);

  const authorize = authorizeQueryFromSigned(bootSearch);

  const startResendWait = (seconds: number) => {
    setResendReadyAt(Date.now() + seconds * 1000);
    setResendWait(seconds);
  };

  // frontend-development: not a fetch; counts down from the deadline (a
  // background tab thins timers, so it is recomputed, not decremented).
  useEffect(() => {
    if (resendReadyAt === null) return;
    const timer = setInterval(() => {
      const left = Math.max(0, Math.ceil((resendReadyAt - Date.now()) / 1000));
      setResendWait(left);
      if (left === 0) setResendReadyAt(null);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendReadyAt]);

  async function finish() {
    if (authorize) {
      window.location.assign(`/api/auth/oauth2/authorize?${authorize}`);
      return;
    }
    const to = await signInDestination(queryClient, api, next);
    // The destination is any path (next), so the history takes it as is.
    router.history.push(to);
  }

  // Conditional UI: passkeys in the email field's autofill. Set once on mount;
  // it resolves only when one is chosen, so the latest `finish` is read by ref.
  const finishRef = useRef(finish);
  finishRef.current = finish;
  // frontend-development: not a fetch; starts the browser's passkey autofill.
  useEffect(() => {
    let alive = true;
    void (async () => {
      const { error } = await signInWithPasskeyAutofill();
      if (!alive || error) return;
      try {
        await session.start();
        await finishRef.current();
      } catch {
        setError(m.web_signin_retry());
      }
    })();
    return () => {
      alive = false;
    };
  }, [session]);

  /** One Turnstile token (undefined without a key); null when it failed. */
  async function captchaToken(): Promise<string | undefined | null> {
    try {
      return await turnstile.getToken();
    } catch {
      setError(m.web_captcha_failed());
      return null;
    }
  }

  async function send(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setNotice(null);
    setPasskeyError(null);
    const address = email.trim();
    if (!looksLikeEmail(address)) {
      setError(m.web_signin_email_invalid());
      return;
    }
    setBusy(true);
    try {
      const token = await captchaToken();
      if (token === null) return;
      const { error } = await sendSignInCode(address, token);
      if (error && error.code !== RESEND_TOO_SOON) {
        setError(otpErrorMessage(error));
        return;
      }
      setSentTo(address);
      setCode("");
      setReusedCode(error !== null);
      startResendWait(error?.retryAfter ?? RESEND_COOLDOWN_SECONDS);
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (busy || resendWait > 0 || !sentTo) return;
    setError(null);
    setCodeInvalid(false);
    setNotice(null);
    setBusy(true);
    try {
      const token = await captchaToken();
      if (token === null) return;
      const { error } = await sendSignInCode(sentTo, token);
      if (error) {
        setError(otpErrorMessage(error));
        if (error.status === 429)
          startResendWait(error.retryAfter ?? RESEND_COOLDOWN_SECONDS);
        return;
      }
      setCode("");
      setNotice(m.web_signin_resent());
      setReusedCode(false);
      startResendWait(RESEND_COOLDOWN_SECONDS);
    } finally {
      setBusy(false);
    }
  }

  async function verify(otp: string) {
    if (busy || !sentTo) return;
    setError(null);
    setCodeInvalid(false);
    setNotice(null);
    setBusy(true);
    try {
      const { error } = await verifySignInCode(sentTo, otp);
      if (error) {
        setError(otpErrorMessage(error));
        setCodeInvalid(true);
        return;
      }
      await session.start();
      await finish();
    } catch {
      setError(m.web_signin_retry());
    } finally {
      setBusy(false);
    }
  }

  async function startGoogle() {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      // Inside an OAuth flow Google comes straight back to authorize.
      const returnTo = authorize
        ? `/api/auth/oauth2/authorize?${authorize}`
        : next
          ? `/signin/done?next=${encodeURIComponent(next)}`
          : "/signin/done";
      const { error } = await signInWithGoogle(returnTo);
      // Coming back here means it never left for Google.
      if (error) setError(m.web_signin_google_failed());
    } finally {
      setBusy(false);
    }
  }

  async function startPasskey() {
    if (busy) return;
    setError(null);
    setNotice(null);
    setPasskeyError(null);
    setBusy(true);
    try {
      const { error } = await signInWithPasskey();
      if (error) {
        setPasskeyError(passkeySignInErrorMessage(error));
        return;
      }
      await session.start();
      await finish();
    } catch {
      setPasskeyError(m.web_signin_retry());
    } finally {
      setBusy(false);
    }
  }

  // Interaction-only: nothing shows unless Cloudflare asks for a check.
  const turnstileSlot = config.data?.turnstileSiteKey ? (
    <div ref={turnstile.containerRef} />
  ) : null;

  if (sentTo)
    return (
      <SignedOutPage title={m.web_signin_title()}>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void verify(code);
          }}
        >
          <p className="text-[13px] leading-[1.7]">
            {(reusedCode ? m.web_signin_code_reused : m.web_signin_code_sent)({
              email: sentTo,
              minutes: CODE_MINUTES,
            })}
          </p>
          <CodeField
            value={code}
            invalid={codeInvalid}
            onChange={(v) => {
              setCode(v);
              setError(null);
              setCodeInvalid(false);
              if (v.length === CODE_LENGTH) void verify(v);
            }}
          />
          <MessageSlot error={error} notice={notice} />
          {turnstileSlot}
          <Button
            type="submit"
            variant="primary"
            className="h-10 w-full"
            disabled={busy || code.length !== CODE_LENGTH}
          >
            {m.web_signin_title()}
          </Button>
          <div className="flex items-center gap-4 self-start">
            <button
              type="button"
              className={TEXT_BUTTON}
              onClick={() => {
                setSentTo(null);
                setError(null);
                setCodeInvalid(false);
                setNotice(null);
                setResendReadyAt(null);
                setResendWait(0);
              }}
            >
              {m.web_signin_other_email()}
            </button>
            <button
              type="button"
              className={TEXT_BUTTON}
              disabled={busy || resendWait > 0}
              onClick={() => void resend()}
            >
              {m.web_signin_resend()}
              {/* Room for "(60)" from the start, so the row never shifts. */}
              <span className="inline-block min-w-[4ch] text-left tabular-nums">
                {resendWait > 0 && `(${resendWait})`}
              </span>
            </button>
          </div>
        </form>
      </SignedOutPage>
    );

  return (
    <SignedOutPage title={m.web_signin_title()}>
      <form className="flex flex-col gap-4" noValidate onSubmit={send}>
        <div className="flex flex-col gap-1">
          <label htmlFor={emailId} className="text-[12px] text-muted">
            {m.web_signin_email_label()}
          </label>
          <input
            id={emailId}
            type="email"
            // `webauthn`: passkeys appear in this field's autofill.
            autoComplete="username webauthn"
            required
            // biome-ignore lint/a11y/noAutofocus: the one thing to do on this page
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={FIELD}
          />
        </div>
        <MessageSlot error={error} notice={m.web_signin_email_hint()} />
        {turnstileSlot}
        <Button
          type="submit"
          variant="primary"
          className="h-10 w-full"
          disabled={busy}
        >
          {m.web_signin_continue()}
        </Button>
      </form>
      {config.data?.google && (
        <button
          type="button"
          className={`${buttonClass("outline")} mt-3 h-10 w-full`}
          disabled={busy}
          onClick={() => void startGoogle()}
        >
          {m.web_signin_google()}
        </button>
      )}
      <div className="mt-4 flex flex-col items-start gap-1">
        <button
          type="button"
          className={TEXT_BUTTON}
          disabled={busy}
          onClick={() => void startPasskey()}
        >
          {m.web_signin_passkey()}
        </button>
        <p
          role={passkeyError ? "alert" : undefined}
          className="min-h-5 text-[12px] leading-5 text-danger"
        >
          {passkeyError}
        </p>
      </div>
    </SignedOutPage>
  );
}

const CELLS = Array.from({ length: CODE_LENGTH }, (_, i) => i);

/**
 * Six cells, one real input. The input lies over the cells, transparent, so
 * one-time-code autofill, paste, select-all and screen readers see one field;
 * the cells only draw. Typing always goes to the end. While an input method
 * composes, the field shows the raw value and tells the parent nothing until
 * the composition ends (changing the parent mid-composition breaks it). The
 * focus is shown by the next cell's border color (the input has no ring).
 */
function CodeField({
  value,
  invalid,
  onChange,
}: {
  value: string;
  invalid: boolean;
  onChange(next: string): void;
}) {
  const id = useId();
  const [composing, setComposing] = useState<string | null>(null);
  // A ref, not state: the input right after compositionend comes in the same batch.
  const composingRef = useRef(false);
  const shown = composing === null ? value : normalizeCode(composing);
  const commit = (raw: string) => {
    composingRef.current = false;
    setComposing(null);
    onChange(normalizeCode(raw));
  };
  const caretToEnd = (el: HTMLInputElement) => {
    const end = el.value.length;
    if (el.selectionStart !== end || el.selectionEnd !== end)
      el.setSelectionRange(end, end);
  };
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[12px] text-muted">
        {m.web_signin_code_label()}
      </label>
      <div className="group relative">
        <div aria-hidden="true" className="grid grid-cols-6 gap-2">
          {CELLS.map((i) => {
            const active = shown.length === i;
            const tone = invalid
              ? "border-danger"
              : active
                ? "border-field group-focus-within:border-accent"
                : "border-field";
            return (
              <div
                key={i}
                className={`flex h-11 items-center justify-center rounded-[7px] border bg-surface text-[16px] text-fg tabular-nums ${tone}`}
              >
                {shown[i] ?? ""}
              </div>
            );
          })}
        </div>
        <input
          id={id}
          className="plain-field absolute inset-0 h-full w-full border-0 bg-transparent text-center text-[16px] text-transparent caret-transparent outline-none selection:bg-transparent"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          data-1p-ignore
          data-lpignore="true"
          data-bwignore
          data-form-type="other"
          required
          // biome-ignore lint/a11y/noAutofocus: right after sending, the code is the only thing to do
          autoFocus
          value={composing ?? value}
          onChange={(e) => {
            const now =
              (e.nativeEvent as InputEvent).isComposing ?? composingRef.current;
            if (now) {
              setComposing(e.target.value);
              return;
            }
            commit(e.target.value);
          }}
          onCompositionStart={(e) => {
            composingRef.current = true;
            setComposing(e.currentTarget.value);
          }}
          onCompositionEnd={(e) => commit(e.currentTarget.value)}
          onFocus={(e) => caretToEnd(e.currentTarget)}
          onClick={(e) => {
            const el = e.currentTarget;
            if (el.selectionStart === el.selectionEnd) caretToEnd(el);
          }}
        />
      </div>
    </div>
  );
}
